import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Language } from "@/utils/translations";

interface LoadingScreenProps {
  type: "generating" | "deciding";
}

const phrases = {
  en: {
    generating: [
      "Consulting my crystal ball... 🔮",
      "Asking the universe... 🌌",
      "Deep in thought... 🤔",
      "Processing quantum calculations... ⚛️",
      "Channeling ancient wisdom... 🧘‍♂️",
    ],
    deciding: [
      "Reading tea leaves... 🍵",
      "Analyzing butterfly effects... 🦋",
      "Consulting with my AI friends... 🤖",
      "Weighing all options... ⚖️",
      "Making the final call... 📞",
    ],
  },
  es: {
    generating: [
      "Consultando mi bola de cristal... 🔮",
      "Preguntando al universo... 🌌",
      "Pensando profundamente... 🤔",
      "Procesando cálculos cuánticos... ⚛️",
      "Canalizando sabiduría antigua... 🧘‍♂️",
    ],
    deciding: [
      "Leyendo las hojas de té... 🍵",
      "Analizando efectos mariposa... 🦋",
      "Consultando con mis amigos IA... 🤖",
      "Sopesando todas las opciones... ⚖️",
      "Tomando la decisión final... 📞",
    ],
  },
  fr: {
    generating: [
      "Je consulte ma boule de cristal... 🔮",
      "Je demande à l'univers... 🌌",
      "En pleine réflexion... 🤔",
      "Calculs quantiques en cours... ⚛️",
      "Canalisation de la sagesse ancienne... 🧘‍♂️",
    ],
    deciding: [
      "Lecture des feuilles de thé... 🍵",
      "Analyse des effets papillon... 🦋",
      "Consultation avec mes amis IA... 🤖",
      "Évaluation de toutes les options... ⚖️",
      "Prise de décision finale... 📞",
    ],
  },
  de: {
    generating: [
      "Ich befrage meine Kristallkugel... 🔮",
      "Ich frage das Universum... 🌌",
      "Tief in Gedanken... 🤔",
      "Quantenberechnungen werden durchgeführt... ⚛️",
      "Channeling alter Weisheit... 🧘‍♂️",
    ],
    deciding: [
      "Teeblätter lesen... 🍵",
      "Schmetterlingseffekte analysieren... 🦋",
      "Beratung mit meinen KI-Freunden... 🤖",
      "Alle Optionen abwägen... ⚖️",
      "Finale Entscheidung treffen... 📞",
    ],
  },
};

export const LoadingScreen = ({ type }: LoadingScreenProps) => {
  const [currentLang, setCurrentLang] = useState<Language>("en");

  useEffect(() => {
    const savedLang = localStorage.getItem("preferredLanguage") as Language;
    if (savedLang) {
      setCurrentLang(savedLang);
    }
  }, []);

  const currentPhrases = phrases[currentLang][type];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center min-h-screen p-4"
    >
      <motion.div
        animate={{
          rotate: 360,
          scale: [1, 1.2, 1],
        }}
        transition={{
          rotate: {
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          },
          scale: {
            duration: 1,
            repeat: Infinity,
            repeatType: "reverse",
          },
        }}
        className="w-24 h-24 mb-8 rounded-full"
        style={{
          background:
            "linear-gradient(to right, hsl(var(--primary) / 0.8), hsl(var(--primary)))",
        }}
      />

      <motion.div
        animate={{ opacity: [0, 1, 0] }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatDelay: 1,
        }}
        className="text-xl font-medium text-gray-700 dark:text-gray-200"
      >
        {currentPhrases[Math.floor(Math.random() * currentPhrases.length)]}
      </motion.div>
    </motion.div>
  );
};
